import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { BUTTON_BORDER_RADIUS_VAR } from '../../context/CustomizationContextProvider'
import { useShopPayFitToWidth } from '../../hooks/useShopPayFitToWidth'
import { ButtonLoadingSpinner } from '../icons/Icons'
import { ShopPayButtonLockupSvg } from './ShopPayButtonLockupSvg'
import { ctaPulse } from '../../style/ctaPulseAnimation'
import { hoverDarkenFill } from '../../style/interactiveStates'
import { getTypographyRoleStyle } from '../../style/typography'
import { SHOP_PAY_DEFAULT_FONT_SIZE } from '../../utils/fitToWidth'

/** Shopify Shop Pay brand purple */
export const SHOP_PAY_PURPLE = '#5A31F4'

const ShopPayButtonRoot = styled.div<{
    $disabled?: boolean
    $fullWidth?: boolean
    $pulse?: boolean
}>(({ $disabled = false, $fullWidth = false, $pulse = false }) => ({
    display: 'flex',
    ...($fullWidth
        ? { width: '100%', flex: 'none', minWidth: 0 }
        : { flex: '1 1 0', minWidth: 0 }),
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 48,
    borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
    backgroundColor: SHOP_PAY_PURPLE,
    color: '#ffffff',
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.5 : 1,
    ...(!$disabled && hoverDarkenFill(SHOP_PAY_PURPLE)),
    ...($pulse &&
        !$disabled && {
            animation: `${ctaPulse} 2.5s ease-in-out infinite`,
            '&:hover': { animationPlayState: 'paused' },
        }),
    ...getTypographyRoleStyle('buttonText', {
        fontWeight: 500,
    }),
    boxSizing: 'border-box',
    padding: '0 12px',
    textAlign: 'center',
}))

type ShopPayCTAButtonProps = {
    onClick: () => void
    disabled?: boolean
    loader?: boolean
    /** Use on cart / stacked layouts so the button spans the row (not flex-grow in a column). */
    fullWidth?: boolean
    /** Controls the label prefix: "Buy with" (default) or "Checkout with". */
    variant?: 'buy' | 'checkout'
    pulse?: boolean
}

export const ShopPayCTAButton: React.FC<ShopPayCTAButtonProps> = ({
    onClick,
    disabled = false,
    loader = true,
    fullWidth = false,
    variant = 'buy',
    pulse = false,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const fittingEnabled = !disabled && !isLoading
    const { containerRef, contentRef, prefixRef, logoRef, fit } =
        useShopPayFitToWidth({
            enabled: fittingEnabled,
            variant,
        })

    useEffect(() => {
        const handlePageShow = () => setIsLoading(false)
        window.addEventListener('pageshow', handlePageShow)
        return () => window.removeEventListener('pageshow', handlePageShow)
    }, [])

    const _onClick = () => {
        if (loader) setIsLoading(true)
        onClick()
    }

    const prefixText =
        variant === 'checkout' ? 'Express Checkout with' : 'Buy with'

    return (
        <ShopPayButtonRoot
            ref={containerRef}
            onClick={disabled ? undefined : _onClick}
            onKeyDown={(e) => {
                if (disabled) return
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    _onClick()
                }
            }}
            role="button"
            tabIndex={disabled ? -1 : 0}
            $disabled={disabled}
            $fullWidth={fullWidth}
            $pulse={pulse}
            aria-label={
                variant === 'checkout'
                    ? 'Express Checkout with Shop Pay'
                    : 'Buy with Shop Pay'
            }
            style={{
                fontSize: fittingEnabled
                    ? fit.fontSize
                    : SHOP_PAY_DEFAULT_FONT_SIZE,
            }}
        >
            {isLoading ? (
                <ButtonLoadingSpinner color="#ffffff" />
            ) : (
                <span
                    ref={contentRef}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        minWidth: 0,
                    }}
                >
                    <span
                        ref={prefixRef}
                        style={{ display: fit.logoOnly ? 'none' : undefined }}
                    >
                        {prefixText}
                    </span>
                    <ShopPayButtonLockupSvg
                        ref={logoRef}
                        height={fit.logoHeight}
                    />
                </span>
            )}
        </ShopPayButtonRoot>
    )
}
